interface DownloadOptions {
  url: string;
  filename?: string;
  method?: 'GET' | 'POST';
  headers?: Record<string, string>;
  body?: any;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  triggerEndpoint?: {
    url: string;
    method?: 'GET' | 'POST';
    body?: any;
  };
}

/**
 * Universal file download utility
 * @param options - Configuration options for the download
 * @returns Promise that resolves when download completes
 */
export const downloadFile = async (options: DownloadOptions): Promise<void> => {
  const {
    url,
    filename,
    method = 'GET',
    headers = {},
    body,
    onSuccess,
    onError,
    triggerEndpoint,
  } = options;

  try {
    // Step 1: Trigger preparation endpoint if provided
    if (triggerEndpoint) {
      const triggerResponse = await fetch(triggerEndpoint.url, {
        method: triggerEndpoint.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: triggerEndpoint.body ? JSON.stringify(triggerEndpoint.body) : undefined,
      });

      if (!triggerResponse.ok) {
        throw new Error(`Failed to trigger preparation endpoint (status: ${triggerResponse.status})`);
      }
    }

    // Step 2: Fetch the file blob
    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw new Error(`Failed to download file (status: ${response.status})`);
    }

    const blob = await response.blob();

    // Step 3: Extract filename from headers or use provided filename
    let finalFilename = filename || 'download';
    const contentDisposition = response.headers.get('Content-Disposition');

    if (contentDisposition && contentDisposition.includes('filename=')) {
      const extractedFilename = contentDisposition
        .split('filename=')[1]
        .replace(/['"]/g, '')
        .trim();
      if (extractedFilename) {
        finalFilename = extractedFilename;
      }
    }

    // Step 4: Create and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', finalFilename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Cleanup
    window.URL.revokeObjectURL(blobUrl);

    // Success callback
    if (onSuccess) {
      onSuccess();
    }
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Unknown error occurred');
    console.error('❌ Error downloading file:', error);

    // Error callback
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
};